<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div
    class="orangehrm-buzz-event-frame"
    :class="{
      'orangehrm-buzz-event-frame--announced': event.status === 'announced',
    }"
  >
    <oxd-text tag="p" class="orangehrm-buzz-event-frame-name">
      {{ event.eventName || $t('buzz.share_event') }}
    </oxd-text>
    <div class="orangehrm-buzz-event-frame-details">
      <div class="orangehrm-buzz-event-frame-detail">
        <oxd-icon
          name="calendar-event"
          class="orangehrm-buzz-event-frame-detail-icon"
        />
        <oxd-text tag="span" class="orangehrm-buzz-event-frame-detail-text">
          {{ formattedDateTime }}
        </oxd-text>
      </div>
      <div v-if="event.location" class="orangehrm-buzz-event-frame-detail">
        <oxd-icon
          name="geo-alt-fill"
          class="orangehrm-buzz-event-frame-detail-icon"
        />
        <a
          v-if="event.link"
          class="orangehrm-buzz-event-frame-detail-link"
          :href="event.link"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ event.location }}
          <oxd-icon
            name="box-arrow-up-right"
            class="orangehrm-buzz-event-frame-detail-link-icon"
          />
        </a>
        <oxd-text
          v-else
          tag="span"
          class="orangehrm-buzz-event-frame-detail-text"
        >
          {{ event.location }}
        </oxd-text>
      </div>
      <div
        v-if="event.status === 'pending'"
        class="orangehrm-buzz-event-frame-detail"
      >
        <oxd-icon
          name="clock-history"
          class="orangehrm-buzz-event-frame-detail-icon"
        />
        <oxd-label
          :label="$t('buzz.event_pending_announcement')"
          class="orangehrm-buzz-event-frame-badge --pending"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {computed} from 'vue';
import useLocale from '@/core/util/composable/useLocale';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import {OxdIcon, OxdLabel} from '@ohrm/oxd';

export default {
  name: 'EventFrame',

  components: {
    'oxd-icon': OxdIcon,
    'oxd-label': OxdLabel,
  },

  props: {
    event: {
      type: Object,
      required: true,
    },
  },

  setup(props) {
    const {locale} = useLocale();

    const formattedDateTime = computed(() => {
      const utcDate = parseDate(
        `${props.event.eventDate} ${props.event.eventTime} +00:00`,
        'yyyy-MM-dd HH:mm xxx',
      );

      return formatDate(utcDate, 'EEE, d MMMM yyyy hh:mm a', {
        locale,
      });
    });

    return {
      formattedDateTime,
    };
  },
};
</script>

<style lang="scss" scoped>
.orangehrm-buzz-event-frame {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid $oxd-interface-gray-lighten-1-color;
  background-color: $oxd-background-pastel-white-color;

  &--announced {
    border-color: #2d6a4f;
    background-color: #1b4332;

    .orangehrm-buzz-event-frame-name {
      color: #ffffff;
    }

    .orangehrm-buzz-event-frame-detail-icon {
      color: rgba(#ffffff, 0.85);
    }

    .orangehrm-buzz-event-frame-detail-text {
      color: rgba(#ffffff, 0.92);
    }

    .orangehrm-buzz-event-frame-detail-link {
      color: #b7e4c7;

      .orangehrm-buzz-event-frame-detail-link-icon {
        color: #ffffff;
      }

      &:hover {
        color: #ffffff;
      }
    }
  }

  &-name {
    font-weight: 700;
    color: $oxd-interface-gray-darken-2-color;
    margin: 0 0 0.5rem;
  }

  &-details {
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    font-family: $oxd-font-family;
  }

  &-detail {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;

    &-icon {
      flex-shrink: 0;
      color: $oxd-interface-gray-color;
      font-size: 0.95rem;
    }

    &-text {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 400;
      color: $oxd-interface-gray-darken-1-color;
    }

    &-link {
      display: inline-flex;
      align-items: center;
      gap: 0.35rem;
      font-size: 0.875rem;
      font-weight: 400;
      font-family: $oxd-font-family;
      color: $oxd-primary-one-color;
      text-decoration: none;
      word-break: break-word;

      &-icon {
        flex-shrink: 0;
        font-size: 0.85rem;
        color: $oxd-primary-one-color;
        filter: brightness(1.35);
      }

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &-badge {
    margin: 0;

    &.--pending {
      :deep(.oxd-label) {
        background-color: $oxd-interface-gray-lighten-2-color;
        color: $oxd-interface-gray-darken-2-color;
      }
    }
  }
}
</style>
